import React, { useState, useEffect } from 'react';
import {
  PlayIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  LightBulbIcon,
  CalculatorIcon,
  ArrowTrendingUpIcon as ChartLineUpIcon,
  BanknotesIcon,
  ChartBarIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

// Define valid exercise types
export type FinanceExerciseType = 
  | 'balance-sheet' 
  | 'dcf-model' 
  | 'ratio-analysis' 
  | 'portfolio-optimization' 
  | 'options-pricing' 
  | 'bond-valuation';

// Helper function to check if exercise type is valid
export const isValidFinanceExerciseType = (type: string): type is FinanceExerciseType => {
  const validTypes: FinanceExerciseType[] = [
    'balance-sheet',
    'dcf-model', 
    'ratio-analysis',
    'portfolio-optimization',
    'options-pricing',
    'bond-valuation'
  ];
  return validTypes.includes(type as FinanceExerciseType);
};

interface FinanceInteractiveProps {
  content: {
    id: string;
    title: string;
    description: string;
    type: string;
    interactiveData: {
      exerciseType: FinanceExerciseType;
      instructions: string;
      scenario?: any;
      expectedResults?: any;
      hints?: string[];
      template?: any;
    };
  };
  onComplete: (completed: boolean, score?: number) => void;
}

const FinanceInteractive: React.FC<FinanceInteractiveProps> = ({
  content,
  onComplete
}) => {
  const [inputs, setInputs] = useState<Record<string, number>>({});
  const [results, setResults] = useState<Record<string, number>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [showHints, setShowHints] = useState(false);
  const [currentHint, setCurrentHint] = useState(0);
  const [calculations, setCalculations] = useState<string[]>([]);
  
  // Tracking and anti-cheating states
  const [startTime] = useState(new Date());
  const [timeSpent, setTimeSpent] = useState(0);
  const [windowFocused, setWindowFocused] = useState(true);
  const [tabSwitches, setTabSwitches] = useState(0);
  const [suspiciousActivity, setSuspiciousActivity] = useState(false);
  const [inputInteractions, setInputInteractions] = useState(0);
  const [lastActiveTime, setLastActiveTime] = useState(Date.now());
  const [minimumEngagementTime] = useState(60); // 1 minute minimum
  const [isEngaged, setIsEngaged] = useState(false);

  const { exerciseType, instructions, scenario, hints } = content.interactiveData;

  // Debug logging and validation for exercise type
  useEffect(() => {
    console.log('FinanceInteractive Debug:', {
      exerciseType,
      contentType: content.type,
      interactiveData: content.interactiveData,
      isValidType: isValidFinanceExerciseType(exerciseType || '')
    });

    if (exerciseType && !isValidFinanceExerciseType(exerciseType)) {
      console.error(`Invalid finance exercise type: "${exerciseType}"`);
      toast.error(`Invalid exercise type: "${exerciseType}". Please check the configuration.`);
    }
  }, [exerciseType, content]);

  useEffect(() => {
    // Initialize inputs based on exercise type
    initializeExercise();
  }, [exerciseType]);

  // Window focus tracking for anti-cheating
  useEffect(() => {
    if (isSubmitted) return;

    const handleFocus = () => {
      setWindowFocused(true);
      setLastActiveTime(Date.now());
    };

    const handleBlur = () => {
      setWindowFocused(false);
      setTabSwitches(prev => {
        const newCount = prev + 1;
        if (newCount >= 3) {
          setSuspiciousActivity(true);
          toast.error('⚠️ Multiple tab switches detected. This may affect your exercise completion.');
        } else if (newCount === 1) {
          toast('⚠️ Stay focused on the exercise for accurate tracking.', {
            icon: '⚠️',
            duration: 3000,
          });
        }
        return newCount;
      });
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setWindowFocused(false);
        // Don't increment tabSwitches here as handleBlur already handles it
      } else {
        setWindowFocused(true);
        setLastActiveTime(Date.now());
      }
    };

    // Keyboard and mouse activity detection
    const handleActivity = () => {
      setLastActiveTime(Date.now());
      if (!isEngaged && timeSpent > 10) { // Mark as engaged after 10 seconds of activity
        setIsEngaged(true);
      }
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('keydown', handleActivity);
    document.addEventListener('mousemove', handleActivity);
    document.addEventListener('click', handleActivity);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('keydown', handleActivity);
      document.removeEventListener('mousemove', handleActivity);
      document.removeEventListener('click', handleActivity);
    };
  }, [isSubmitted, timeSpent, isEngaged]);

  // Time tracking
  useEffect(() => {
    if (isSubmitted) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const timeSinceActivity = now - lastActiveTime;
      
      // Only count time if user is active (within last 30 seconds) and window is focused
      if (windowFocused && timeSinceActivity < 30000) {
        setTimeSpent(prev => prev + 1);
      }
      
      // Warn if user has been inactive for too long
      if (timeSinceActivity > 60000 && windowFocused) {
        toast('💡 Still working on the exercise? Move your mouse to continue tracking.', {
          duration: 4000,
        });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isSubmitted, windowFocused, lastActiveTime]);

  // Engagement validation
  useEffect(() => {
    if (isSubmitted) return;
    
    const checkEngagement = setInterval(() => {
      if (timeSpent >= minimumEngagementTime && !isEngaged) {
        setIsEngaged(true);
        toast.success('✅ Good engagement detected! You can now submit your answers.');
      }
    }, 5000);

    return () => clearInterval(checkEngagement);
  }, [timeSpent, minimumEngagementTime, isEngaged, isSubmitted]);

  const initializeExercise = () => {
    if (!exerciseType) {
      console.warn('FinanceInteractive: No exerciseType provided');
      setInputs({});
      return;
    }

    switch (exerciseType) {
      case 'balance-sheet':
        setInputs({
          retainedEarnings: 0
        });
        break;
      case 'dcf-model':
        setInputs({
          year1FCF: 0,
          year2FCF: 0,
          year3FCF: 0,
          year4FCF: 0,
          year5FCF: 0,
          terminalValue: 0,
          presentValue: 0,
          intrinsicValue: 0
        });
        break;
      case 'ratio-analysis':
        setInputs({
          currentRatio: 0,
          quickRatio: 0,
          debtToEquity: 0,
          roe: 0,
          roa: 0
        });
        break;
      case 'portfolio-optimization':
        setInputs({
          stockWeight: 0,
          bondWeight: 0,
          reitWeight: 0,
          expectedReturn: 0,
          portfolioRisk: 0
        });
        break;
      case 'options-pricing':
        setInputs({
          callPrice: 0,
          putPrice: 0,
          delta: 0,
          gamma: 0,
          theta: 0
        });
        break;
      case 'bond-valuation':
        setInputs({
          bondPrice: 0,
          yieldToMaturity: 0,
          duration: 0,
          convexity: 0
        });
        break;
      default:
        console.warn(`FinanceInteractive: Unknown exercise type "${exerciseType}"`);
        setInputs({});
        break;
    }
  };

  const handleInputChange = (key: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    setInputs(prev => ({
      ...prev,
      [key]: numValue
    }));
    
    // Track input interactions for engagement
    setInputInteractions(prev => prev + 1);
    setLastActiveTime(Date.now());
    
    // Mark as engaged after meaningful interactions
    if (inputInteractions > 3 && timeSpent > 20) {
      setIsEngaged(true);
    }
  };

  const calculateBalanceSheet = () => {
    const assets = scenario?.assets || {
      cash: 50000,
      accountsReceivable: 25000,
      inventory: 30000,
      equipment: 100000
    };
    
    const liabilities = scenario?.liabilities || {
      accountsPayable: 15000,
      notesPayable: 40000,
      longTermDebt: 75000
    };
    
    const equity = scenario?.equity || {
      commonStock: 40000
    };

    const totalAssets = Object.values(assets).reduce((sum: number, val: any) => sum + val, 0);
    const totalLiabilities = Object.values(liabilities).reduce((sum: number, val: any) => sum + val, 0);
    const calculatedRetainedEarnings = totalAssets - totalLiabilities - equity.commonStock;

    setResults({
      totalAssets,
      totalLiabilities,
      calculatedRetainedEarnings,
      totalEquity: equity.commonStock + inputs.retainedEarnings,
      isBalanced: totalAssets === (totalLiabilities + equity.commonStock + inputs.retainedEarnings) ? 1 : 0
    });

    setCalculations([
      `Total Assets: ${totalAssets.toLocaleString()}`,
      `Total Liabilities: ${totalLiabilities.toLocaleString()}`,
      `Common Stock: ${equity.commonStock.toLocaleString()}`,
      `Required Retained Earnings: ${calculatedRetainedEarnings.toLocaleString()}`,
      `Your Retained Earnings: ${inputs.retainedEarnings.toLocaleString()}`,
      `Balance Check: ${totalAssets.toLocaleString()} = ${(totalLiabilities + equity.commonStock + inputs.retainedEarnings).toLocaleString()}`
    ]);

    return Math.abs(inputs.retainedEarnings - calculatedRetainedEarnings) < 0.01;
  };

  const calculateDCF = () => {
    const companyData = scenario?.companyData || {
      currentFCF: 1000,
      growthRates: [0.20, 0.18, 0.15, 0.12, 0.10],
      wacc: 0.10,
      terminalGrowthRate: 0.03,
      sharesOutstanding: 100
    };

    let projectedFCF = [];
    let currentFCF = companyData.currentFCF;
    
    for (let i = 0; i < companyData.growthRates.length; i++) {
      currentFCF = currentFCF * (1 + companyData.growthRates[i]);
      projectedFCF.push(currentFCF);
    }

    let presentValueFCF = 0;
    for (let i = 0; i < projectedFCF.length; i++) {
      const pv = projectedFCF[i] / Math.pow(1 + companyData.wacc, i + 1);
      presentValueFCF += pv;
    }

    const terminalFCF = projectedFCF[projectedFCF.length - 1] * (1 + companyData.terminalGrowthRate);
    const terminalValue = terminalFCF / (companyData.wacc - companyData.terminalGrowthRate);
    const presentValueTerminal = terminalValue / Math.pow(1 + companyData.wacc, projectedFCF.length);
    const enterpriseValue = presentValueFCF + presentValueTerminal;
    const intrinsicValuePerShare = enterpriseValue / companyData.sharesOutstanding;

    setResults({
      projectedFCFLength: projectedFCF.length,
      presentValueFCF: Math.round(presentValueFCF),
      terminalValue: Math.round(terminalValue),
      presentValueTerminal: Math.round(presentValueTerminal),
      enterpriseValue: Math.round(enterpriseValue),
      intrinsicValuePerShare: Math.round(intrinsicValuePerShare * 100) / 100
    });

    setCalculations([
      `Projected FCF: [${projectedFCF.map(f => f.toFixed(1)).join(', ')}]`,
      `Present Value of FCF: ${Math.round(presentValueFCF).toLocaleString()}`,
      `Terminal Value: ${Math.round(terminalValue).toLocaleString()}`,
      `Present Value of Terminal: ${Math.round(presentValueTerminal).toLocaleString()}`,
      `Enterprise Value: ${Math.round(enterpriseValue).toLocaleString()}`,
      `Intrinsic Value per Share: $${intrinsicValuePerShare.toFixed(2)}`
    ]);

    return Math.abs(inputs.intrinsicValue - intrinsicValuePerShare) < 1;
  };

  const calculateRatios = () => {
    const financialData = scenario?.financialData || {
      currentAssets: 150000,
      inventory: 30000,
      currentLiabilities: 75000,
      totalDebt: 200000,
      totalEquity: 300000,
      netIncome: 45000,
      totalAssets: 500000
    };

    const correctCurrentRatio = financialData.currentAssets / financialData.currentLiabilities;
    const correctQuickRatio = (financialData.currentAssets - financialData.inventory) / financialData.currentLiabilities;
    const correctDebtToEquity = financialData.totalDebt / financialData.totalEquity;
    const correctROE = (financialData.netIncome / financialData.totalEquity) * 100;
    const correctROA = (financialData.netIncome / financialData.totalAssets) * 100;

    setResults({
      correctCurrentRatio: Math.round(correctCurrentRatio * 100) / 100,
      correctQuickRatio: Math.round(correctQuickRatio * 100) / 100,
      correctDebtToEquity: Math.round(correctDebtToEquity * 100) / 100,
      correctROE: Math.round(correctROE * 10) / 10,
      correctROA: Math.round(correctROA * 10) / 10
    });

    setCalculations([
      `Current Ratio: ${financialData.currentAssets.toLocaleString()} ÷ ${financialData.currentLiabilities.toLocaleString()} = ${correctCurrentRatio.toFixed(2)}`,
      `Quick Ratio: (${financialData.currentAssets.toLocaleString()} - ${financialData.inventory.toLocaleString()}) ÷ ${financialData.currentLiabilities.toLocaleString()} = ${correctQuickRatio.toFixed(2)}`,
      `Debt-to-Equity: ${financialData.totalDebt.toLocaleString()} ÷ ${financialData.totalEquity.toLocaleString()} = ${correctDebtToEquity.toFixed(2)}`,
      `ROE: (${financialData.netIncome.toLocaleString()} ÷ ${financialData.totalEquity.toLocaleString()}) × 100 = ${correctROE.toFixed(1)}%`,
      `ROA: (${financialData.netIncome.toLocaleString()} ÷ ${financialData.totalAssets.toLocaleString()}) × 100 = ${correctROA.toFixed(1)}%`
    ]);

    const tolerance = 0.1;
    return (
      Math.abs(inputs.currentRatio - correctCurrentRatio) < tolerance &&
      Math.abs(inputs.quickRatio - correctQuickRatio) < tolerance &&
      Math.abs(inputs.debtToEquity - correctDebtToEquity) < tolerance &&
      Math.abs(inputs.roe - correctROE) < tolerance &&
      Math.abs(inputs.roa - correctROA) < tolerance
    );
  };

  const calculatePortfolio = () => {
    const assets = scenario?.assets || [
      { symbol: "SPY", expectedReturn: 0.10, volatility: 0.16, name: "S&P 500 ETF" },
      { symbol: "BND", expectedReturn: 0.04, volatility: 0.05, name: "Total Bond Market ETF" },
      { symbol: "VEA", expectedReturn: 0.08, volatility: 0.18, name: "International Developed ETF" },
      { symbol: "VNQ", expectedReturn: 0.07, volatility: 0.20, name: "Real Estate ETF" }
    ];

    const totalWeight = inputs.stockWeight + inputs.bondWeight + inputs.reitWeight;
    
    if (Math.abs(totalWeight - 100) > 1) {
      setResults({});
      setCalculations([`Error: Total weights: ${totalWeight.toFixed(1)}% (should be 100%)`]);
      return false;
    }

    const stockWeight = inputs.stockWeight / 100;
    const bondWeight = inputs.bondWeight / 100;
    const reitWeight = inputs.reitWeight / 100;

    const portfolioReturn = (stockWeight * assets[0].expectedReturn) + 
                          (bondWeight * assets[1].expectedReturn) + 
                          (reitWeight * assets[3].expectedReturn);

    // Simplified portfolio risk calculation (assumes correlations)
    const stockVar = Math.pow(stockWeight * assets[0].volatility, 2);
    const bondVar = Math.pow(bondWeight * assets[1].volatility, 2);
    const reitVar = Math.pow(reitWeight * assets[3].volatility, 2);
    const portfolioRisk = Math.sqrt(stockVar + bondVar + reitVar + 
                                  2 * stockWeight * bondWeight * 0.15 * assets[0].volatility * assets[1].volatility +
                                  2 * stockWeight * reitWeight * 0.65 * assets[0].volatility * assets[3].volatility +
                                  2 * bondWeight * reitWeight * 0.35 * assets[1].volatility * assets[3].volatility);

    const sharpeRatio = (portfolioReturn - 0.02) / portfolioRisk; // Assuming 2% risk-free rate

    setResults({
      portfolioReturn: Math.round(portfolioReturn * 10000) / 100,
      portfolioRisk: Math.round(portfolioRisk * 10000) / 100,
      sharpeRatio: Math.round(sharpeRatio * 100) / 100
    });

    setCalculations([
      `Expected Return: ${(portfolioReturn * 100).toFixed(2)}%`,
      `Portfolio Risk: ${(portfolioRisk * 100).toFixed(2)}%`,
      `Sharpe Ratio: ${sharpeRatio.toFixed(2)}`,
      `Asset Allocation: ${inputs.stockWeight}% Stocks, ${inputs.bondWeight}% Bonds, ${inputs.reitWeight}% REITs`
    ]);

    return Math.abs(inputs.expectedReturn - portfolioReturn * 100) < 0.5 && 
           Math.abs(inputs.portfolioRisk - portfolioRisk * 100) < 0.5;
  };

  const calculateOptions = () => {
    const params = scenario?.optionParams || {
      stockPrice: 100,
      strike: 100,
      timeToExpiration: 30/365, // 30 days
      volatility: 0.25,
      riskFreeRate: 0.05,
      dividendYield: 0.02
    };

    // Black-Scholes calculations
    const S = params.stockPrice;
    const K = params.strike;
    const T = params.timeToExpiration;
    const r = params.riskFreeRate;
    const q = params.dividendYield;
    const sigma = params.volatility;

    const d1 = (Math.log(S/K) + (r - q + 0.5 * sigma * sigma) * T) / (sigma * Math.sqrt(T));
    const d2 = d1 - sigma * Math.sqrt(T);

    // Normal cumulative distribution function approximation
    const normalCDF = (x: number) => {
      const t = 1 / (1 + 0.2316419 * Math.abs(x));
      const d = 0.3989423 * Math.exp(-x * x / 2);
      const prob = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
      return x > 0 ? 1 - prob : prob;
    };

    const Nd1 = normalCDF(d1);
    const Nd2 = normalCDF(d2);
    const Nmd1 = normalCDF(-d1);
    const Nmd2 = normalCDF(-d2);

    const callPrice = S * Math.exp(-q * T) * Nd1 - K * Math.exp(-r * T) * Nd2;
    const putPrice = K * Math.exp(-r * T) * Nmd2 - S * Math.exp(-q * T) * Nmd1;

    // Greeks
    const callDelta = Math.exp(-q * T) * Nd1;
    const putDelta = Math.exp(-q * T) * (Nd1 - 1);
    const gamma = Math.exp(-q * T) / (S * sigma * Math.sqrt(T)) * Math.exp(-d1 * d1 / 2) / Math.sqrt(2 * Math.PI);
    const callTheta = -(S * Math.exp(-q * T) * Math.exp(-d1 * d1 / 2) / Math.sqrt(2 * Math.PI) * sigma) / (2 * Math.sqrt(T)) - 
                     r * K * Math.exp(-r * T) * Nd2 + q * S * Math.exp(-q * T) * Nd1;
    const putTheta = -(S * Math.exp(-q * T) * Math.exp(-d1 * d1 / 2) / Math.sqrt(2 * Math.PI) * sigma) / (2 * Math.sqrt(T)) + 
                    r * K * Math.exp(-r * T) * Nmd2 - q * S * Math.exp(-q * T) * Nmd1;

    setResults({
      callPrice: Math.round(callPrice * 100) / 100,
      putPrice: Math.round(putPrice * 100) / 100,
      callDelta: Math.round(callDelta * 1000) / 1000,
      putDelta: Math.round(putDelta * 1000) / 1000,
      gamma: Math.round(gamma * 10000) / 10000,
      callTheta: Math.round(callTheta * 100) / 100,
      putTheta: Math.round(putTheta * 100) / 100
    });

    setCalculations([
      `Call Price: $${callPrice.toFixed(2)}`,
      `Put Price: $${putPrice.toFixed(2)}`,
      `Call Delta: ${callDelta.toFixed(3)}`,
      `Put Delta: ${putDelta.toFixed(3)}`,
      `Gamma: ${gamma.toFixed(4)}`,
      `Call Theta: ${callTheta.toFixed(2)}`,
      `Put Theta: ${putTheta.toFixed(2)}`
    ]);

    const tolerance = 0.50;
    return Math.abs(inputs.callPrice - callPrice) < tolerance && 
           Math.abs(inputs.putPrice - putPrice) < tolerance &&
           Math.abs(inputs.delta - callDelta) < 0.05 &&
           Math.abs(inputs.gamma - gamma) < 0.01 &&
           Math.abs(inputs.theta - callTheta) < 1.0;
  };

  const calculateBondValuation = () => {
    const params = scenario?.bondParams || {
      faceValue: 1000,
      couponRate: 0.06, // 6% annual coupon
      maturity: 5, // 5 years
      marketYield: 0.05 // 5% market yield
    };

    const FV = params.faceValue;
    const c = params.couponRate;
    const n = params.maturity;
    const y = params.marketYield;

    const annualCoupon = FV * c;
    
    // Bond price calculation
    let pv = 0;
    for (let t = 1; t <= n; t++) {
      pv += annualCoupon / Math.pow(1 + y, t);
    }
    pv += FV / Math.pow(1 + y, n);

    // Duration calculation (Macaulay Duration)
    let weightedCashFlows = 0;
    for (let t = 1; t <= n; t++) {
      const cashFlow = annualCoupon / Math.pow(1 + y, t);
      weightedCashFlows += t * cashFlow;
    }
    weightedCashFlows += n * (FV / Math.pow(1 + y, n));
    const macaulayDuration = weightedCashFlows / pv;
    const modifiedDuration = macaulayDuration / (1 + y);

    // Convexity calculation
    let convexitySum = 0;
    for (let t = 1; t <= n; t++) {
      const cashFlow = annualCoupon / Math.pow(1 + y, t);
      convexitySum += (t * (t + 1) * cashFlow) / Math.pow(1 + y, 2);
    }
    convexitySum += (n * (n + 1) * FV / Math.pow(1 + y, n)) / Math.pow(1 + y, 2);
    const convexity = convexitySum / pv;

    // Yield to Maturity (YTM) - approximate using current inputs
    const ytm = y; // For simplicity, using market yield as YTM

    setResults({
      bondPrice: Math.round(pv * 100) / 100,
      yieldToMaturity: Math.round(ytm * 10000) / 100,
      macaulayDuration: Math.round(macaulayDuration * 100) / 100,
      modifiedDuration: Math.round(modifiedDuration * 100) / 100,
      convexity: Math.round(convexity * 100) / 100
    });

    setCalculations([
      `Bond Price: $${pv.toFixed(2)}`,
      `Yield to Maturity: ${(ytm * 100).toFixed(2)}%`,
      `Macaulay Duration: ${macaulayDuration.toFixed(2)} years`,
      `Modified Duration: ${modifiedDuration.toFixed(2)}`,
      `Convexity: ${convexity.toFixed(2)}`,
      `Annual Coupon: $${annualCoupon.toFixed(2)}`
    ]);

    const tolerance = 5.0;
    return Math.abs(inputs.bondPrice - pv) < tolerance && 
           Math.abs(inputs.yieldToMaturity - ytm * 100) < 0.1 &&
           Math.abs(inputs.duration - modifiedDuration) < 0.2 &&
           Math.abs(inputs.convexity - convexity) < 2.0;
  };

  const handleSubmit = () => {
    // Validation checks before submission
    if (!isEngaged && timeSpent < minimumEngagementTime) {
      toast.error(`Please spend at least ${minimumEngagementTime} seconds working on the exercise before submitting.`);
      return;
    }

    if (tabSwitches >= 5) {
      toast.error('Too many tab switches detected. This may indicate unauthorized assistance.');
      return;
    }

    if (inputInteractions < 3) {
      toast.error('Please interact with the exercise inputs before submitting.');
      return;
    }

    // Anti-cheating: Check for suspiciously fast completion
    if (timeSpent < 30 && inputInteractions > 10) {
      setSuspiciousActivity(true);
      toast('⚠️ Unusually fast completion detected. Please take time to understand the exercise.', {
        icon: '⚠️',
        duration: 4000,
      });
    }

    let correct = false;

    if (!exerciseType) {
      toast.error('No exercise type specified. Cannot submit.');
      return;
    }

    switch (exerciseType) {
      case 'balance-sheet':
        correct = calculateBalanceSheet();
        break;
      case 'dcf-model':
        correct = calculateDCF();
        break;
      case 'ratio-analysis':
        correct = calculateRatios();
        break;
      case 'portfolio-optimization':
        correct = calculatePortfolio();
        break;
      case 'options-pricing':
        correct = calculateOptions();
        break;
      case 'bond-valuation':
        correct = calculateBondValuation();
        break;
      default:
        toast.error(`Unknown exercise type: "${exerciseType}". Cannot submit.`);
        return;
    }

    setIsSubmitted(true);
    setIsCorrect(correct);

    // Log completion data
    const completionData = {
      exerciseType,
      timeSpent,
      tabSwitches,
      inputInteractions,
      suspiciousActivity,
      windowFocused,
      engagementTime: timeSpent,
      startTime: startTime.toISOString(),
      endTime: new Date().toISOString(),
      userAgent: navigator.userAgent
    };

    console.log('Finance Exercise Completion Data:', completionData);

    if (correct) {
      toast.success('Excellent! Your calculations are correct!');
      onComplete(true, suspiciousActivity ? 85 : 100); // Reduce score if suspicious activity
    } else {
      toast.error('Some calculations need adjustment. Check the hints!');
      onComplete(false, 0);
    }
  };

  const handleReset = () => {
    setIsSubmitted(false);
    setIsCorrect(false);
    setShowHints(false);
    setCurrentHint(0);
    setResults({});
    setCalculations([]);
    initializeExercise();
  };

  const nextHint = () => {
    if (hints && currentHint < hints.length - 1) {
      setCurrentHint(currentHint + 1);
    }
  };

  const renderExercise = () => {
    switch (exerciseType) {
      case 'balance-sheet':
        return renderBalanceSheetExercise();
      case 'dcf-model':
        return renderDCFExercise();
      case 'ratio-analysis':
        return renderRatioAnalysisExercise();
      case 'portfolio-optimization':
        return renderPortfolioOptimizationExercise();
      case 'options-pricing':
        return renderOptionsPricingExercise();
      case 'bond-valuation':
        return renderBondValuationExercise();
      default:
        return (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h4 className="font-semibold text-red-800 mb-2">❌ Unknown Exercise Type</h4>
            <p className="text-red-700 mb-3">
              Exercise type "{exerciseType}" is not recognized. Please check the configuration.
            </p>
            <div className="bg-red-100 p-3 rounded text-sm">
              <strong>Supported exercise types:</strong>
              <ul className="mt-2 space-y-1 list-disc list-inside">
                <li>balance-sheet</li>
                <li>dcf-model</li>
                <li>ratio-analysis</li>
                <li>portfolio-optimization</li>
                <li>options-pricing</li>
                <li>bond-valuation</li>
              </ul>
              <p className="mt-2">
                <strong>Current value:</strong> "{exerciseType}"
              </p>
            </div>
          </div>
        );
    }
  };

  const renderBalanceSheetExercise = () => (
    <div className="space-y-6">
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-semibold mb-3">📊 Company Financial Data</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h5 className="font-medium text-green-700 mb-2">Assets</h5>
            <ul className="space-y-1 text-sm">
              <li>Cash: $50,000</li>
              <li>Accounts Receivable: $25,000</li>
              <li>Inventory: $30,000</li>
              <li>Equipment: $100,000</li>
            </ul>
          </div>
          <div>
            <h5 className="font-medium text-red-700 mb-2">Liabilities & Equity</h5>
            <ul className="space-y-1 text-sm">
              <li>Accounts Payable: $15,000</li>
              <li>Notes Payable: $40,000</li>
              <li>Long-term Debt: $75,000</li>
              <li>Common Stock: $40,000</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Calculate Retained Earnings to balance the sheet:</span>
          <div className="mt-1 relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
            <input
              type="number"
              value={inputs.retainedEarnings || ''}
              onChange={(e) => handleInputChange('retainedEarnings', e.target.value)}
              className="pl-8 w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter retained earnings..."
            />
          </div>
        </label>
      </div>

      {results.totalAssets && (
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-semibold mb-3">📋 Balance Sheet Check</h4>
          <div className="space-y-2 text-sm">
            {calculations.map((calc, index) => (
              <div key={index} className="font-mono">{calc}</div>
            ))}
            <div className={`font-bold mt-3 ${results.isBalanced ? 'text-green-600' : 'text-red-600'}`}>
              {results.isBalanced ? '✅ Balance Sheet is BALANCED!' : '❌ Balance Sheet is NOT balanced'}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderDCFExercise = () => (
    <div className="space-y-6">
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-semibold mb-3">🏢 TechCorp Inc. - DCF Valuation</h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <strong>Current FCF:</strong> $1,000M
          </div>
          <div>
            <strong>Shares Outstanding:</strong> 100M
          </div>
          <div>
            <strong>WACC:</strong> 10%
          </div>
          <div>
            <strong>Terminal Growth:</strong> 3%
          </div>
        </div>
        <div className="mt-2">
          <strong>Growth Rates:</strong> Year 1: 20%, Year 2: 18%, Year 3: 15%, Year 4: 12%, Year 5: 10%
        </div>
      </div>

      <div className="space-y-4">
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Intrinsic Value per Share:</span>
          <div className="mt-1 relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
            <input
              type="number"
              step="0.01"
              value={inputs.intrinsicValue || ''}
              onChange={(e) => handleInputChange('intrinsicValue', e.target.value)}
              className="pl-8 w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter intrinsic value per share..."
            />
          </div>
        </label>
      </div>

      {results.enterpriseValue && (
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-semibold mb-3">📊 DCF Calculation Results</h4>
          <div className="space-y-2 text-sm">
            {calculations.map((calc, index) => (
              <div key={index} className="font-mono">{calc}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderRatioAnalysisExercise = () => (
    <div className="space-y-6">
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-semibold mb-3">📈 Company Financial Data for Ratio Analysis</h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <strong>Current Assets:</strong> $150,000
          </div>
          <div>
            <strong>Inventory:</strong> $30,000
          </div>
          <div>
            <strong>Current Liabilities:</strong> $75,000
          </div>
          <div>
            <strong>Total Debt:</strong> $200,000
          </div>
          <div>
            <strong>Total Equity:</strong> $300,000
          </div>
          <div>
            <strong>Net Income:</strong> $45,000
          </div>
          <div>
            <strong>Total Assets:</strong> $500,000
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Current Ratio:</span>
          <input
            type="number"
            step="0.01"
            value={inputs.currentRatio || ''}
            onChange={(e) => handleInputChange('currentRatio', e.target.value)}
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g. 2.00"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">Quick Ratio:</span>
          <input
            type="number"
            step="0.01"
            value={inputs.quickRatio || ''}
            onChange={(e) => handleInputChange('quickRatio', e.target.value)}
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g. 1.60"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">Debt-to-Equity Ratio:</span>
          <input
            type="number"
            step="0.01"
            value={inputs.debtToEquity || ''}
            onChange={(e) => handleInputChange('debtToEquity', e.target.value)}
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g. 0.67"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">ROE (%):</span>
          <input
            type="number"
            step="0.1"
            value={inputs.roe || ''}
            onChange={(e) => handleInputChange('roe', e.target.value)}
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g. 15.0"
          />
        </label>

        <label className="block md:col-span-2">
          <span className="text-sm font-medium text-gray-700">ROA (%):</span>
          <input
            type="number"
            step="0.1"
            value={inputs.roa || ''}
            onChange={(e) => handleInputChange('roa', e.target.value)}
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g. 9.0"
          />
        </label>
      </div>

      {results.correctCurrentRatio && (
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-semibold mb-3">📊 Ratio Analysis Results</h4>
          <div className="space-y-2 text-sm">
            {calculations.map((calc, index) => (
              <div key={index} className="font-mono">{calc}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderPortfolioOptimizationExercise = () => (
    <div className="space-y-6">
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-semibold mb-3">📊 Portfolio Optimization Challenge</h4>
        <p className="text-sm text-gray-700 mb-3">
          Build an optimal portfolio using the available assets. Your goal is to maximize risk-adjusted returns (Sharpe ratio) while maintaining proper diversification.
        </p>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <strong>Available Assets:</strong>
            <ul className="mt-1 space-y-1">
              <li>• SPY (S&P 500): 10% return, 16% volatility</li>
              <li>• BND (Bonds): 4% return, 5% volatility</li>
              <li>• VEA (Intl): 8% return, 18% volatility</li>
              <li>• VNQ (REITs): 7% return, 20% volatility</li>
            </ul>
          </div>
          <div>
            <strong>Correlations:</strong>
            <ul className="mt-1 space-y-1">
              <li>• Stocks-Bonds: 0.15</li>
              <li>• Stocks-REITs: 0.65</li>
              <li>• Bonds-REITs: 0.35</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Stock Weight (%):</span>
          <input
            type="number"
            min="0"
            max="100"
            step="1"
            value={inputs.stockWeight || ''}
            onChange={(e) => handleInputChange('stockWeight', e.target.value)}
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g. 60"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">Bond Weight (%):</span>
          <input
            type="number"
            min="0"
            max="100"
            step="1"
            value={inputs.bondWeight || ''}
            onChange={(e) => handleInputChange('bondWeight', e.target.value)}
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g. 30"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">REIT Weight (%):</span>
          <input
            type="number"
            min="0"
            max="100"
            step="1"
            value={inputs.reitWeight || ''}
            onChange={(e) => handleInputChange('reitWeight', e.target.value)}
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g. 10"
          />
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Expected Portfolio Return (%):</span>
          <input
            type="number"
            step="0.1"
            value={inputs.expectedReturn || ''}
            onChange={(e) => handleInputChange('expectedReturn', e.target.value)}
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g. 8.2"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">Portfolio Risk (%):</span>
          <input
            type="number"
            step="0.1"
            value={inputs.portfolioRisk || ''}
            onChange={(e) => handleInputChange('portfolioRisk', e.target.value)}
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g. 12.5"
          />
        </label>
      </div>

      {results.portfolioReturn && (
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-semibold mb-3">📈 Portfolio Analysis Results</h4>
          <div className="space-y-2 text-sm">
            {calculations.map((calc, index) => (
              <div key={index} className="font-mono">{calc}</div>
            ))}
            {results.sharpeRatio && (
              <div className={`font-bold mt-3 ${results.sharpeRatio > 0.6 ? 'text-green-600' : 'text-orange-600'}`}>
                Sharpe Ratio: {results.sharpeRatio} {results.sharpeRatio > 0.6 ? '(Excellent!)' : '(Could be improved)'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderOptionsPricingExercise = () => (
    <div className="space-y-6">
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-semibold mb-3">📈 Options Pricing & Greeks Analysis</h4>
        <p className="text-sm text-gray-700 mb-3">
          Calculate option prices and Greeks using the Black-Scholes model. Use the given parameters to price call and put options.
        </p>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <strong>Market Data:</strong>
            <ul className="mt-1 space-y-1">
              <li>• Stock Price: $100</li>
              <li>• Strike Price: $100</li>
              <li>• Time to Expiration: 30 days</li>
              <li>• Risk-free Rate: 5%</li>
            </ul>
          </div>
          <div>
            <strong>Parameters:</strong>
            <ul className="mt-1 space-y-1">
              <li>• Volatility: 25%</li>
              <li>• Dividend Yield: 2%</li>
              <li>• Calculate: Price & Greeks</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Call Option Price ($):</span>
          <input
            type="number"
            step="0.01"
            value={inputs.callPrice || ''}
            onChange={(e) => handleInputChange('callPrice', e.target.value)}
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g. 3.25"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">Put Option Price ($):</span>
          <input
            type="number"
            step="0.01"
            value={inputs.putPrice || ''}
            onChange={(e) => handleInputChange('putPrice', e.target.value)}
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g. 2.50"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">Delta:</span>
          <input
            type="number"
            step="0.001"
            value={inputs.delta || ''}
            onChange={(e) => handleInputChange('delta', e.target.value)}
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g. 0.520"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">Gamma:</span>
          <input
            type="number"
            step="0.0001"
            value={inputs.gamma || ''}
            onChange={(e) => handleInputChange('gamma', e.target.value)}
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g. 0.0195"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">Theta (per day):</span>
          <input
            type="number"
            step="0.01"
            value={inputs.theta || ''}
            onChange={(e) => handleInputChange('theta', e.target.value)}
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g. -0.05"
          />
        </label>
      </div>

      {results.callPrice && (
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-semibold mb-3">⚙️ Options Pricing Results</h4>
          <div className="space-y-2 text-sm">
            {calculations.map((calc, index) => (
              <div key={index} className="font-mono">{calc}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderBondValuationExercise = () => (
    <div className="space-y-6">
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-semibold mb-3">🏦 Bond Valuation & Risk Analysis</h4>
        <p className="text-sm text-gray-700 mb-3">
          Calculate bond price and risk metrics for a corporate bond. Use present value techniques and duration analysis.
        </p>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <strong>Bond Details:</strong>
            <ul className="mt-1 space-y-1">
              <li>• Face Value: $1,000</li>
              <li>• Coupon Rate: 6% annual</li>
              <li>• Maturity: 5 years</li>
              <li>• Annual Coupons: $60</li>
            </ul>
          </div>
          <div>
            <strong>Market Conditions:</strong>
            <ul className="mt-1 space-y-1">
              <li>• Current Market Yield: 5%</li>
              <li>• Calculate: Price & Duration</li>
              <li>• Risk Metrics: Convexity</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Bond Price ($):</span>
          <input
            type="number"
            step="0.01"
            value={inputs.bondPrice || ''}
            onChange={(e) => handleInputChange('bondPrice', e.target.value)}
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g. 1043.30"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">Yield to Maturity (%):</span>
          <input
            type="number"
            step="0.01"
            value={inputs.yieldToMaturity || ''}
            onChange={(e) => handleInputChange('yieldToMaturity', e.target.value)}
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g. 5.00"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">Modified Duration:</span>
          <input
            type="number"
            step="0.01"
            value={inputs.duration || ''}
            onChange={(e) => handleInputChange('duration', e.target.value)}
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g. 4.33"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">Convexity:</span>
          <input
            type="number"
            step="0.01"
            value={inputs.convexity || ''}
            onChange={(e) => handleInputChange('convexity', e.target.value)}
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g. 20.45"
          />
        </label>
      </div>

      {results.bondPrice && (
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-semibold mb-3">💰 Bond Valuation Results</h4>
          <div className="space-y-2 text-sm">
            {calculations.map((calc, index) => (
              <div key={index} className="font-mono">{calc}</div>
            ))}
            {results.modifiedDuration && (
              <div className="font-bold mt-3 text-blue-600">
                Price Sensitivity: 1% yield change = {results.modifiedDuration?.toFixed(2)}% price change
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const getExerciseIcon = () => {
    switch (exerciseType) {
      case 'balance-sheet': return <BanknotesIcon className="h-6 w-6" />;
      case 'dcf-model': return <ChartLineUpIcon className="h-6 w-6" />;
      case 'ratio-analysis': return <ChartBarIcon className="h-6 w-6" />;
      case 'portfolio-optimization': return <ChartBarIcon className="h-6 w-6" />;
      case 'options-pricing': return <CalculatorIcon className="h-6 w-6" />;
      case 'bond-valuation': return <BanknotesIcon className="h-6 w-6" />;
      default: return <CalculatorIcon className="h-6 w-6" />;
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          {getExerciseIcon()}
          <div className="ml-3">
            <h3 className="text-lg font-semibold text-gray-900">{content.title}</h3>
            <p className="text-sm text-gray-600">{content.description}</p>
          </div>
        </div>
        
        {/* Tracking Status Indicators */}
        <div className="flex items-center space-x-4 text-sm">
          {/* Focus Indicator */}
          <div className={`flex items-center px-2 py-1 rounded-full ${
            windowFocused ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            <div className={`w-2 h-2 rounded-full mr-1 ${
              windowFocused ? 'bg-green-500' : 'bg-red-500'
            }`}></div>
            {windowFocused ? 'Focused' : 'Not Focused'}
          </div>
          
          {/* Time Tracker */}
          <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
            ⏱️ {formatTime(timeSpent)}
          </div>
          
          {/* Tab Switches Warning */}
          {tabSwitches > 0 && (
            <div className={`px-2 py-1 rounded-full ${
              tabSwitches >= 3 ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'
            }`}>
              ⚠️ {tabSwitches} switch{tabSwitches !== 1 ? 'es' : ''}
            </div>
          )}
          
          {/* Engagement Indicator */}
          {!isEngaged && !isSubmitted && (
            <div className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
              💡 Working...
            </div>
          )}
          
          {isEngaged && !isSubmitted && (
            <div className="bg-green-100 text-green-800 px-2 py-1 rounded-full">
              ✅ Engaged
            </div>
          )}
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h4 className="font-medium text-blue-900 mb-2">📋 Instructions</h4>
        <p className="text-blue-800 text-sm">{instructions}</p>
      </div>

      {renderExercise()}

      <div className="flex items-center justify-between mt-8">
        <div className="flex space-x-4">
          <button
            onClick={handleSubmit}
            disabled={isSubmitted && isCorrect || (!isEngaged && timeSpent < minimumEngagementTime)}
            className={`flex items-center px-6 py-2 rounded-lg font-medium transition-colors ${
              isSubmitted && isCorrect
                ? 'bg-green-100 text-green-600 cursor-not-allowed'
                : (!isEngaged && timeSpent < minimumEngagementTime)
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : tabSwitches >= 3
                ? 'bg-orange-600 text-white hover:bg-orange-700'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
            title={!isEngaged && timeSpent < minimumEngagementTime 
              ? `Please work on the exercise for at least ${minimumEngagementTime} seconds` 
              : tabSwitches >= 3
              ? 'Warning: Multiple tab switches detected'
              : ''
            }
          >
            {isSubmitted && isCorrect ? (
              <>
                <CheckCircleIcon className="h-5 w-5 mr-2" />
                Completed
              </>
            ) : (!isEngaged && timeSpent < minimumEngagementTime) ? (
              <>
                <ClockIcon className="h-5 w-5 mr-2" />
                Keep Working ({minimumEngagementTime - timeSpent}s)
              </>
            ) : tabSwitches >= 3 ? (
              <>
                <XCircleIcon className="h-5 w-5 mr-2" />
                Submit (Warning)
              </>
            ) : (
              <>
                <PlayIcon className="h-5 w-5 mr-2" />
                Submit
              </>
            )}
          </button>

          <button
            onClick={handleReset}
            className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <ArrowPathIcon className="h-4 w-4 mr-2" />
            Reset
          </button>
        </div>

        <div className="flex items-center space-x-4">
          {hints && hints.length > 0 && (
            <button
              onClick={() => setShowHints(!showHints)}
              className="flex items-center px-4 py-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
            >
              <LightBulbIcon className="h-4 w-4 mr-2" />
              Hints ({hints.length})
            </button>
          )}

          {isSubmitted && (
            <div className={`flex items-center ${isCorrect ? 'text-green-600' : 'text-red-600'}`}>
              {isCorrect ? (
                <CheckCircleIcon className="h-5 w-5 mr-1" />
              ) : (
                <XCircleIcon className="h-5 w-5 mr-1" />
              )}
              <span className="text-sm font-medium">
                {isCorrect ? 'Correct!' : 'Try again'}
              </span>
            </div>
          )}
        </div>
      </div>

      {showHints && hints && hints.length > 0 && (
        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-yellow-900">💡 Hint {currentHint + 1} of {hints.length}</h4>
            {currentHint < hints.length - 1 && (
              <button
                onClick={nextHint}
                className="text-yellow-600 hover:text-yellow-800 text-sm underline"
              >
                Next hint →
              </button>
            )}
          </div>
          <p className="text-yellow-800 text-sm">{hints[currentHint]}</p>
        </div>
      )}
    </div>
  );
};

export default FinanceInteractive;